
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Route, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from '@/components/ui/sonner';

interface RentalMileage {
  id: string;
  human_number: string;
  courierName: string;
  startDate: string;
  endDate: string | null;
  status: string;
  totalMileage: number;
}

interface RentalMileageHistoryProps {
  courierId?: string;
  limit?: number;
}

export const RentalMileageHistory: React.FC<RentalMileageHistoryProps> = ({ 
  courierId, 
  limit = 10 
}) => {
  const [rentals, setRentals] = useState<RentalMileage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState<string | null>(null);

  const fetchRentalMileageHistory = async () => {
    setLoading(true);
    
    try {
      let query = supabase
        .from('rentals')
        .select(`
          id,
          human_number,
          start_date,
          end_date,
          status,
          total_mileage,
          couriers!inner(name)
        `)
        .order('start_date', { ascending: false })
        .limit(limit);

      if (courierId) {
        query = query.eq('courier_id', courierId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedRentals: RentalMileage[] = data.map((rental: any) => ({
        id: rental.id,
        human_number: rental.human_number || `#${rental.id.substring(0, 8)}`,
        courierName: rental.couriers?.name || 'Неизвестный курьер',
        startDate: rental.start_date,
        endDate: rental.end_date,
        status: rental.status,
        totalMileage: Number(rental.total_mileage || 0)
      }));

      setRentals(formattedRentals);
    } catch (err) {
      console.error('Ошибка при загрузке истории пробега:', err);
      toast.error('Не удалось загрузить историю пробега');
    } finally {
      setLoading(false);
    }
  };

  const recalculateMileage = async (rentalId: string) => {
    try {
      const { error } = await supabase.rpc('update_total_rental_mileage', {
        rental_id_param: rentalId
      });

      if (error) throw error;

      toast.success('Пробег пересчитан');
      await fetchRentalMileageHistory();
    } catch (err) {
      console.error('Ошибка при пересчете пробега:', err);
      toast.error('Не удалось пересчитать пробег');
    }
  };

  const formatMileage = (mileage: number): string => {
    return `${mileage.toFixed(1)} км`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: ru });
    } catch {
      return 'Неверная дата';
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { variant: 'default' as const, label: 'Активна' },
      completed: { variant: 'secondary' as const, label: 'Завершена' },
      cancelled: { variant: 'destructive' as const, label: 'Отменена' },
      pending: { variant: 'outline' as const, label: 'Ожидает' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || 
                   { variant: 'outline' as const, label: status };

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  useEffect(() => {
    fetchRentalMileageHistory();
  }, [courierId, limit]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Route className="h-5 w-5 text-tuktuk-teal" />
          История пробега по арендам
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRentalMileageHistory}
          disabled={loading}
          className="flex items-center gap-1"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="text-center py-6">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            <div className="text-muted-foreground">Загрузка истории пробега...</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Аренда</TableHead>
                {!courierId && <TableHead>Курьер</TableHead>}
                <TableHead>Период</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Пробег</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rentals.length > 0 ? (
                rentals.map((rental) => (
                  <TableRow key={rental.id}>
                    <TableCell className="font-medium">
                      {rental.human_number}
                    </TableCell>
                    {!courierId && (
                      <TableCell>{rental.courierName}</TableCell>
                    )}
                    <TableCell>
                      <div className="text-sm">
                        <div>{formatDate(rental.startDate)}</div>
                        {rental.endDate && (
                          <div className="text-muted-foreground">
                            до {formatDate(rental.endDate)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(rental.status)}
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-tuktuk-teal">
                        {formatMileage(rental.totalMileage)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => recalculateMileage(rental.id)}
                          className="flex items-center gap-1"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Пересчитать
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={courierId ? 5 : 6} 
                    className="text-center py-6 text-muted-foreground"
                  >
                    История пробега не найдена
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
